import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulation d'envoi (sera remplacé par l'API backend)
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success('Message envoyé avec succès!', {
        description: 'Notre équipe vous contactera dans les plus brefs délais.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    }, 1500);
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">
            Contactez-nous
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Parlons de votre projet
          </h2>
          <p className="text-xl text-gray-600">
            Notre équipe est à votre écoute pour étudier vos besoins et vous proposer la meilleure solution
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Address */}
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="bg-gradient-to-br from-primary to-cyan-600 text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <MapPin size={24} aria-hidden="true" />
                </div>
                <CardTitle>Notre adresse</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{companyInfo.address}</p>
              </CardContent>
            </Card>

            {/* Phone */}
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Phone size={24} aria-hidden="true" />
                </div>
                <CardTitle>Téléphone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {companyInfo.phone.map((phone, index) => (
                  <a
                    key={index}
                    href={`tel:${phone}`}
                    className="block text-gray-600 hover:text-primary transition-colors"
                  >
                    {phone}
                  </a>
                ))}
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="bg-gradient-to-br from-blue-500 to-primary text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Mail size={24} aria-hidden="true" />
                </div>
                <CardTitle>Email</CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-600 hover:text-primary transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 border-2">
            <CardHeader>
              <CardTitle className="text-2xl">Demandez un devis gratuit</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-gray-700">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jean@exemple.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+236 XX XX XX XX"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-medium text-gray-700">
                      Entreprise / Organisation
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Nom de votre entreprise"
                      value={formData.company}
                      onChange={handleChange}
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="service" className="text-sm font-medium text-gray-700">
                    Service souhaité <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-3 rounded-md border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="déroulants">Panneaux Déroulants</option>
                    <option value="led">Panneaux LED</option>
                    <option value="grand-format">Panneaux Grand Format</option>
                    <option value="personnalisé">Solution personnalisée</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium text-gray-700">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Décrivez votre projet et vos besoins..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer le message
                      <Send size={20} className="ml-2" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
